import { useMemo } from "react";

import { Color } from "@vericus/cadmus-ui";

import dayjs from "dayjs";

import { dateColors, isDateInvalid } from "@/ui/DatePicker";

/** Selection step */
export enum Step {
  Final,
  Return,
  Draft,
}

/** Minimum and maximum selectable dates for the calendar. */
export interface StepDateBounds {
  minDate?: Date;
  maxDate?: Date;
}

/**
 * Compute the valid pickable date range for the current step.
 */
export function useStepDateBounds(
  step: Step,
  dueDate: Date | null
): StepDateBounds {
  return useMemo(() => {
    const now = new Date();
    switch (step) {
      // due date - from now onwards
      case Step.Final:
        return { minDate: now };

      // return date - from now / dueDate onwards
      case Step.Return:
        return {
          minDate: dueDate && dayjs(dueDate).isAfter(now) ? dueDate : now,
        };

      // draft due date - from now onwards until due date
      case Step.Draft:
        return { minDate: now, maxDate: dueDate ?? undefined };
    }
  }, [step, dueDate]);
}

/**
 * Validate setting dueDate
 * dueDate has to be from now onwards.
 */
export const validateSettingDueDate = (
  dueDate: Date,
  onError: (e: string) => void,
  onSuccess: () => void,
  now: Date = new Date()
): void => {
  if (
    isDateInvalid(dueDate, [now], [], () =>
      onError("final due set in the past")
    )
  )
    return;

  onSuccess();
};

/**
 * Validate setting returnDate
 * returnDate has to be from now and dueDate onwards.
 */
export const validateSettingReturnDate = (
  dueDate: Date,
  returnDate: Date,
  onError: (e: string) => void,
  onSuccess: () => void,
  now: Date = new Date()
): void => {
  if (
    isDateInvalid(returnDate, [now], [], () =>
      onError("return date set in the past")
    )
  )
    return;

  if (
    isDateInvalid(returnDate, [dueDate], [], () =>
      onError("return date before final due")
    )
  )
    return;

  onSuccess();
};

/**
 * Validate setting draftDueDate
 * draftDueDate has to be from now onwards until dueDate.
 */
export const validateSettingDraftDueDate = (
  dueDate: Date,
  draftDue: Date,
  onError: (e: string) => void,
  onSuccess: () => void,
  now: Date = new Date()
): void => {
  if (
    isDateInvalid(draftDue, [now], [], () =>
      onError("draft due set in the past")
    )
  )
    return;
  if (
    isDateInvalid(draftDue, [], [dueDate], undefined, () =>
      onError("draft due after final due")
    )
  )
    return;

  onSuccess();
};

/**
 * Data for `Header` in `TaskTimeline`.
 */
export const STEP_CONFIG: Record<
  Step,
  {
    number: number;
    text: string;
    color: Color;
    afterText: string;
    description: string;
  }
> = {
  [Step.Final]: {
    number: 1,
    text: "final",
    color: dateColors.dueDate,
    afterText: "due date",
    description:
      "Before the due date, students can re-submit as many times as they like. No re-submissions are allowed after the due date. Students can still submit late if they haven’t made any previous submissions.",
  },
  [Step.Return]: {
    number: 2,
    text: "feedback return",
    color: dateColors.returnDate,
    afterText: "date",
    description:
      "After this date and time, students can access their grades and feedback within Cadmus.",
  },
  [Step.Draft]: {
    number: 3,
    text: "draft",
    color: dateColors.draftDueDate,
    afterText: "due date",
    description:
      "Before the draft due date, students can re-submit as many times as they like. No submissions are allowed after the draft due date.",
  },
};
